/**
 * Ticket Quiz recommendation engine (pure JS, no DOM).
 *
 * Ported from the "Qual é a sua noite no Carnaval do Rio? — Demo v3" prototype.
 * Inventory is injected via createEngine({ inv }) instead of the demo's SNAP/FRISA constants.
 *
 * Inventory row: { day: 'YYYY-MM-DD', k: catalogKey, sec: string, row: string|null, p: price, av: available }
 * Catalog keys: arq, arq9, cadeira, frisa, folia, mar
 *
 * CommonJS on purpose: runs in plain Node tests and is bundled by webpack/Mix.
 */

const TAU = 0.9;
const THR = 0.82;
const THR_REFINE = 0.97;

const C = ['arq', 'arq9', 'cadeira', 'frisa', 'folia', 'mar'];
const CAMA = ['folia', 'mar'];

const DEFAULT_DAYS = {
  '2027-02-05': { g: 'ouro', w: 'Sex', n: 5, time: '21h' },
  '2027-02-06': { g: 'ouro', w: 'Sáb', n: 6, time: '21h' },
  '2027-02-07': { g: 'especial', w: 'Dom', n: 7, time: '22h' },
  '2027-02-08': { g: 'especial', w: 'Seg', n: 8, time: '22h' },
  '2027-02-09': { g: 'especial', w: 'Ter', n: 9, time: '22h' },
  '2027-02-13': { g: 'campeas', w: 'Sáb', n: 13, time: '21h' },
};
const TIER = { ouro: 1, campeas: 2, especial: 3 };

/* Origin prior (light): international market only */
const DEFAULT_ORIGINS = [
  { id: 'latam', pts: { arq: 2.5, arq9: 1, frisa: 1, folia: 0.8, cadeira: 0.5, mar: 0.3 } },
  { id: 'mx', pts: { frisa: 2, cadeira: 1.5, arq9: 1, mar: 0.8, folia: 0.8, arq: 0.6 } },
  { id: 'us', pts: { cadeira: 2, arq9: 1.6, mar: 1.6, frisa: 1.2, folia: 0.8, arq: 0.6 } },
  { id: 'ca', pts: { arq: 2, arq9: 1.5, cadeira: 1, frisa: 0.8, folia: 0.6, mar: 0.4 } },
  { id: 'uk', pts: { cadeira: 1.8, arq9: 1.6, frisa: 1.2, mar: 1, folia: 0.8, arq: 0.8 } },
  { id: 'fr', pts: { arq: 2.5, arq9: 1.5, cadeira: 0.8, frisa: 0.6, folia: 0.4, mar: 0.3 } },
  { id: 'dach', pts: { arq: 2.5, arq9: 1.5, cadeira: 0.8, frisa: 0.5, folia: 0.4, mar: 0.3 } },
  { id: 'eu', pts: { arq: 2.2, arq9: 1.4, cadeira: 0.9, frisa: 0.8, folia: 0.5, mar: 0.4 } },
  { id: 'oc', pts: { cadeira: 1.8, arq9: 1.4, frisa: 1.2, mar: 1, folia: 0.8, arq: 0.8 } },
  { id: 'as', pts: { cadeira: 1.8, frisa: 1.5, mar: 1.4, arq9: 1.2, folia: 0.5, arq: 0.4 } },
  { id: 'ot', pts: { arq: 1, arq9: 1, cadeira: 1, frisa: 1, folia: 1, mar: 1 } },
];

/* Sector "vibes" used to offer position choices when several sectors tie on price */
const POS = [
  { id: 'start', secs: ['2', '3', '4'] },
  { id: 'drum1', secs: ['2', '3'] },
  { id: 'center', secs: ['6', '7', '8', '9'] },
  { id: 'drum2', secs: ['8', '9', '10', '11'] },
  { id: 'local', secs: ['5', '11', '12', '13'] },
  { id: 'end', secs: ['12', '13'] },
];

/* Questions the engine chooses dynamically, with prior answer weights */
const QPOOL = ['budget', 'seat', 'view', 'festa', 'crew'];
const QA = {
  budget: [[60, 0.15], [100, 0.2], [150, 0.2], [250, 0.2], [450, 0.15], [900, 0.1]],
  seat: [['marcado', 0.4], ['livre', 0.25], ['tanto', 0.35]],
  view: [['perto', 0.3], ['alto', 0.35], ['tanto', 0.35]],
  festa: [['inclusa', 0.15], ['desfile', 0.45], ['tanto', 0.4]],
  crew: [['solo', 0.15], ['casal', 0.3], ['amigos', 0.35], ['criancas', 0.12], ['idosos', 0.08]],
};

/* Relaxation sets, smallest first: which hard filters to drop when nothing survives */
function buildRelaxSets() {
  const k = ['budget', 'days', 'seat', 'festa'];
  const out = [];
  for (let n = 1; n <= k.length; n++) {
    const rec = (i, cur) => {
      if (cur.length === n) { out.push(cur.slice()); return; }
      for (let j = i; j < k.length; j++) { cur.push(k[j]); rec(j + 1, cur); cur.pop(); }
    };
    rec(0, []);
  }
  return out;
}
const RELAX_SETS = buildRelaxSets();

function rowScore(row, view) {
  if (!row) return 0;
  const i = 'ABCDEFGH'.indexOf(row);
  if (i < 0) return 0;
  if (view === 'perto') return [3, 2, 0.5, 0, -1, -1, -1, -1][i];
  if (view === 'alto') return [0, 1, 2, 2, 0.5, 0.5, 0.5, 0.5][i];
  return [0.6, 1, 0.8, 0.5, 0, 0, 0, 0][i];
}

function rowOK(row, view) {
  if (!row) return true;
  if (view === 'perto') return 'AB'.includes(row);
  if (view === 'alto') return 'CD'.includes(row);
  return true;
}

function createEngine({ inv = [], daysMeta } = {}) {
  const DAYS = daysMeta || DEFAULT_DAYS;
  const ALLDAYS = Object.keys(DAYS);
  const INV = inv.filter((r) => r && r.av > 0);

  const tierOf = (day) => (DAYS[day] && TIER[DAYS[day].g]) || 1;
  const daysSel = (a) => (a.days && a.days.length ? a.days : ALLDAYS);
  const rowsOf = (k, days) => INV.filter((r) => r.k === k && days.includes(r.day));
  const minPrice = (k, days) => {
    const r = rowsOf(k, days);
    return r.length ? Math.min(...r.map((x) => x.p)) : Infinity;
  };
  const priceOk = (p, a) => {
    if (a.budget !== undefined && p > a.budget) return false;
    if (a.budgetMin !== undefined && p < a.budgetMin) return false;
    return true;
  };
  const hasBudget = (a) => a.budget !== undefined || a.budgetMin !== undefined;

  /* Hard filters: what an answer eliminates */
  function viableC(c, a, rx) {
    rx = rx || {};
    const days = rx.days ? ALLDAYS : daysSel(a);
    const rows = rowsOf(c, days);
    if (!rows.length) return false;
    if (!rx.budget && hasBudget(a) && !rows.some((r) => priceOk(r.p, a))) return false;
    if (!rx.seat && a.seat === 'marcado' && (c === 'arq' || CAMA.includes(c))) return false;
    if (!rx.festa && a.festa === 'inclusa' && !CAMA.includes(c)) return false;
    if (!rx.festa && a.festa === 'desfile' && CAMA.includes(c)) return false;
    if (a.crew === 'criancas' && c === 'mar') return false; // Camarote Mar is 18+ only
    if (a.box === 'nao' && c === 'frisa') return false; // refused a shared box of 6
    if (a.crew === 'idosos') {
      // Comfort means an assigned seat. The accessible camarote is Folia, and only when they asked for the party.
      if (a.festa === 'inclusa') {
        if (c !== 'folia') return false;
      } else if (c === 'arq' || CAMA.includes(c)) {
        return false;
      }
    }
    return true;
  }

  /* Preferences: what an answer only scores */
  function soft(c, a, rx) {
    let s = 0;
    const add = (m) => { if (m[c]) s += m[c]; };
    if (a.view === 'perto') add({ frisa: 3, cadeira: 1.5, mar: 1, folia: 1 });
    if (a.view === 'alto') add({ arq: 2, arq9: 2, cadeira: 2, frisa: 0.8 });
    if (a.seat === 'livre') add({ arq: 1.5, folia: 1, mar: 1 });
    if (a.seat === 'marcado') add({ arq9: 1, cadeira: 1.5, frisa: 1.5 });
    if (a.camarote === 'folia') add({ folia: 2.5 });
    if (a.camarote === 'mar') add({ mar: 2.5 });
    const cr = a.crew;
    if (cr === 'idosos') add({ cadeira: 2, frisa: 1, folia: 1, arq: -1 });
    if (cr === 'criancas') add({ cadeira: 2, arq9: 1.5, frisa: 0.5, folia: 0.5, arq: -0.5 });
    if (cr === 'casal') add({ frisa: 1, mar: 0.5, folia: 0.5 });
    if (cr === 'amigos') add({ arq: 1, folia: 1.5, mar: 0.5 });
    if (cr === 'solo') add({ arq: 0.5, folia: 0.5 });
    if (hasBudget(a) && rx && rx.budget) {
      s -= minPrice(c, daysSel(a)) / 150;
    }
    return s;
  }

  function computeState(a) {
    let rx = {};
    let relaxed = [];
    let surv = C.filter((c) => viableC(c, a, rx));
    if (!surv.length) {
      for (const set of RELAX_SETS) {
        const r = {};
        set.forEach((x) => { r[x] = true; });
        const s = C.filter((c) => viableC(c, a, r));
        if (s.length) { rx = r; relaxed = set; surv = s; break; }
      }
    }
    if (!surv.length) {
      surv = C.filter((c) => {
        if (a.box === 'nao' && c === 'frisa') return false;
        return INV.some((r) => r.k === c);
      });
    }
    if (!surv.length) return { surv: [], probs: [], top: null, conf: 0, H: 0, relaxed, rx };
    const sc = surv.map((c) => soft(c, a, rx) / TAU);
    const mx = Math.max(...sc);
    const ex = sc.map((s) => Math.exp(s - mx));
    const Z = ex.reduce((x, y) => x + y, 0);
    const order = surv.map((c, i) => ({ c, p: ex[i] / Z })).sort((x, y) => y.p - x.p);
    let H = 0;
    order.forEach((x) => { if (x.p > 0) H -= x.p * Math.log(x.p); });
    return { surv, probs: order, top: order[0].c, conf: order[0].p, H, relaxed, rx };
  }

  /* Next question = the one with the lowest expected entropy over its answers */
  function pickNextQ(a, asked) {
    let best = null;
    for (const q of QPOOL) {
      if (asked.includes(q)) continue;
      let e = 0;
      for (const [v, w] of QA[q]) {
        const s = computeState({ ...a, [q]: v });
        e += w * (s.H + (s.relaxed.length ? 0.7 : 0));
      }
      if (!best || e < best.e - 1e-9) best = { q, e };
    }
    return best ? best.q : null;
  }

  const coreAsked = (asked) => asked.filter((q) => QPOOL.includes(q));
  const remainingCore = (asked) => QPOOL.filter((q) => !asked.includes(q)).length;

  function shouldStop(a, asked) {
    return nextStep(a, asked) === 'result';
  }

  const MARKED = ['arq9', 'cadeira', 'frisa'];
  const FREE = ['arq', 'folia', 'mar'];
  const HIGH = ['arq', 'arq9'];
  const CLOSE = ['frisa', 'cadeira', 'folia', 'mar'];

  function hasAny(keys, group) {
    return keys.some((k) => group.includes(k));
  }

  function aliveKeys(a) {
    return C.filter((c) => viableC(c, a, {}));
  }

  /* Fixed sales order. Budget is always asked. Later questions appear only when they still change the ticket. */
  function nextStep(a, asked) {
    asked = asked || [];
    if (!asked.includes('days')) return 'days';
    if (!asked.includes('festa')) return 'festa';
    if (!asked.includes('crew')) return 'crew';
    if (!asked.includes('budget')) return 'budget';

    const alive = aliveKeys(a);
    if (!alive.length) return 'result';
    if (!asked.includes('seat') && hasAny(alive, MARKED) && hasAny(alive, FREE)) return 'seat';
    if (!asked.includes('view') && hasAny(alive, HIGH) && hasAny(alive, CLOSE)) return 'view';
    if (!asked.includes('box') && alive.includes('frisa') && (a.view !== 'alto' || computeState(a).top === 'frisa')) return 'box';
    if (!asked.includes('camarote') && alive.includes('folia') && alive.includes('mar')) return 'camarote';
    return 'result';
  }

  /* Slider bounds for whatever is still viable, before the person narrows the price. */
  function priceBounds(a) {
    const open = Object.assign({}, a);
    delete open.budget;
    delete open.budgetMin;
    const days = daysSel(open);
    const keys = aliveKeys(open);
    const prices = INV
      .filter((r) => keys.includes(r.k) && days.includes(r.day) && r.p > 0)
      .map((r) => r.p);
    if (!prices.length) return { min: 30, max: 1500 };
    const min = Math.max(30, Math.floor(Math.min.apply(null, prices) / 10) * 10);
    let max = Math.ceil(Math.max.apply(null, prices) / 10) * 10;
    if (max <= min) max = min + 10;
    return { min: min, max: max };
  }

  /* True when the top pick is a camarote and the other camarote is a close second */
  function camaroteTie(a) {
    const st = computeState(a);
    if (!CAMA.includes(st.top)) return false;
    const other = st.top === 'folia' ? 'mar' : 'folia';
    const o = st.probs.find((x) => x.c === other);
    return !!o && o.p >= 0.25 * st.conf;
  }

  /* Pick the best inventory row (night / sector / row) for a catalog key */
  function pickSku(k, a) {
    const days = daysSel(a);
    let rows = rowsOf(k, days);
    if (!rows.length) rows = INV.filter((r) => r.k === k);
    if (!rows.length) return null;
    let fit = hasBudget(a) ? rows.filter((r) => priceOk(r.p, a)) : rows;
    const over = hasBudget(a) && !fit.length;
    if (!fit.length) fit = rows;
    const ref = Math.max(a.budget || 200, 60);
    const datesOpen = !(a.days && a.days.length);
    let best = null;
    for (const r of fit) {
      let s = (datesOpen ? 0 : tierOf(r.day) * 0.8) - (r.p / ref) * 1.2 + (Math.min(r.av, 300) / 300) * 0.5 + rowScore(r.row, a.view);
      if (a.positionSecs && a.positionSecs.length) s += a.positionSecs.includes(r.sec) ? 3 : -3;
      if (over) s = -r.p / 50;
      s -= (+r.sec) * 0.001;
      if (!best || s > best.s) best = { s, r };
    }
    return { ...best.r, over };
  }

  /* Sectors that tie on price for the chosen night (non-camarote only) */
  function tieSectors(k, a) {
    if (CAMA.includes(k)) return [];
    const base = pickSku(k, { ...a, positionSecs: [] });
    if (!base) return [];
    let rows = INV.filter((r) => r.k === k && r.day === base.day && rowOK(r.row, a.view));
    if (hasBudget(a)) {
      const fit = rows.filter((r) => priceOk(r.p, a));
      if (fit.length) rows = fit;
    }
    if (!rows.length) return [];
    const by = {};
    rows.forEach((r) => { by[r.sec] = Math.min(by[r.sec] === undefined ? 1e9 : by[r.sec], r.p); });
    const m = Math.min(...Object.values(by));
    return Object.keys(by).filter((s) => by[s] <= m * 1.12 + 1);
  }

  /* Position choices (ids from POS + matching sectors) when sectors tie; [] if no real choice */
  function positionOptions(a) {
    const top = computeState(a).top;
    if (!top) return [];
    const T = tieSectors(top, a);
    if (T.length < 2) return [];
    const seen = new Set();
    const out = [];
    for (const g of POS) {
      const hit = g.secs.filter((s) => T.includes(s));
      if (!hit.length || hit.length === T.length) continue;
      const key = hit.slice().sort().join(',');
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ ...g, hit });
    }
    return out.length >= 2 ? out.slice(0, 4) : [];
  }

  return {
    computeState,
    pickNextQ,
    nextStep,
    priceBounds,
    shouldStop,
    camaroteTie,
    pickSku,
    tieSectors,
    positionOptions,
    remainingCore,
    coreAsked,
    viableC,
    soft,
  };
}

module.exports = {
  createEngine,
  constants: { TAU, THR, THR_REFINE, C, CAMA, QPOOL, QA, POS, DEFAULT_DAYS, DEFAULT_ORIGINS },
};
